import type { Vector } from "@common/utils/vector";

export interface ShorePoint extends Vector { nx: number; ny: number }

/** Split real bank edges into short runs. River mouths and overlaps are not shorelines. */
export function shorelineRuns(points: readonly Vector[], river: boolean, waterAt?: (point: Vector) => boolean, spacing = 2): ShorePoint[][] {
    if (points.length < 3) return [];
    const area = points.reduce((sum, p, i) => {
        const next = points[(i + 1) % points.length];
        return sum + p.x * next.y - next.x * p.y;
    }, 0);
    const paths = river ? [points.slice(0, points.length / 2), points.slice(points.length / 2)] : [[...points, points[0]]];
    const runs: ShorePoint[][] = [];
    for (const path of paths) {
        let run: ShorePoint[] = [];
        for (let i = 1; i < path.length; i++) {
            const a = path[i - 1], b = path[i];
            const dx = b.x - a.x, dy = b.y - a.y, length = Math.hypot(dx, dy);
            if (!length) continue;
            const steps = Math.ceil(length / Math.max(0.5, spacing));
            for (let j = 0; j < steps; j++) {
                const x = a.x + dx * j / steps, y = a.y + dy * j / steps;
                let nx = -dy / length, ny = dx / length;
                // Sample across the corner bisector, rather than along the incoming edge.
                if (j === 0 && (!river || i > 1)) {
                    const previous = path[i > 1 ? i - 2 : path.length - 2];
                    const px = a.x - previous.x, py = a.y - previous.y;
                    const pl = Math.hypot(px, py);
                    if (pl) {
                        const bx = nx - py / pl, by = ny + px / pl;
                        const bl = Math.hypot(bx, by);
                        if (bl > 0.1) { nx = bx / bl; ny = by / bl; }
                    }
                }
                if (waterAt) {
                    const left = waterAt({ x: x + nx * 2, y: y + ny * 2 });
                    const right = waterAt({ x: x - nx * 2, y: y - ny * 2 });
                    if (left === right) {
                        if (run.length > 1) runs.push(run);
                        run = [];
                        continue;
                    }
                    if (!left) { nx = -nx; ny = -ny; }
                } else {
                    const sign = (area > 0 ? -1 : 1) * (river ? -1 : 1);
                    nx *= sign; ny *= sign;
                }
                run.push({ x, y, nx, ny });
            }
        }
        if (run.length > 1) runs.push(run);
    }
    return runs;
}

/** Coordinate-based variation stays identical in the world and on the map. */
export function shoreVariation(p: Vector, seed = 0): number {
    return 0.5 + 0.25 * Math.sin(p.x * 0.31 + p.y * 0.19 + seed)
        + 0.25 * Math.sin(p.x * 0.11 - p.y * 0.27 + seed * 2.3);
}

/** Clamp a decorative offset to its terrain side, including narrow river bends. */
export function shoreOffset(p: ShorePoint, distance: number, sideAt?: (point: Vector) => boolean): Vector | undefined {
    for (let attempt = 0; attempt < 6; attempt++) {
        const q = { x: p.x + p.nx * distance, y: p.y + p.ny * distance };
        if (!sideAt || sideAt(q)) return q;
        distance *= 0.5;
    }
}

/** Short filled ribbons cannot join separated mouths. */
export function shoreRibbons(run: readonly ShorePoint[], inner: (p: ShorePoint, i: number) => number,
    outer: (p: ShorePoint, i: number) => number, sideAt?: (point: Vector) => boolean): Vector[][] {
    const polygons: Vector[][] = [];
    let left: Vector[] = [], right: Vector[] = [];
    const flush = (): void => {
        if (left.length > 1) polygons.push([...left, ...right.reverse()]);
        left = []; right = [];
    };
    for (let i = 0; i < run.length; i++) {
        const p = run[i], a = shoreOffset(p, inner(p, i), sideAt), b = shoreOffset(p, outer(p, i), sideAt);
        if (!a || !b) { flush(); continue; }
        left.push(a); right.push(b);
        if (left.length === 48) { flush(); left.push(a); right.push(b); }
    }
    flush();
    return polygons;
}

/** Distribute a fixed detail budget over the entire coastline. */
export function shoreDetails(runs: readonly (readonly ShorePoint[])[], budget: number, stride: number): ShorePoint[] {
    const candidates = runs.flatMap(run => run.filter((_, i) => i % stride === 0));
    const count = Math.min(budget, candidates.length);
    return Array.from({ length: count }, (_, i) => candidates[Math.floor(i * candidates.length / count)]);
}
